import { ALIAS_KEY, CONTROLLER_KEY, CONTROLLER_NAME_KEY, EVENT_ATTACHED, EVENT_DETACHED, TARGET_ATTRIBUTE, } from './Constants';
class DomController {
    constructor() {
        this.resolver = this.defaultResolver;
        this.EVENT_ATTACHED = EVENT_ATTACHED;
        this.EVENT_DETACHED = EVENT_DETACHED;
        this.resolved = {};
        this.resolvedHrefs = {};
        this.observer = new MutationObserver((mutations) => this.mutationHandler(mutations));
        const htmlNode = document.querySelector('html');
        this.observer.observe(htmlNode, {
            attributes: true,
            subtree: true,
            childList: true,
            attributeFilter: [TARGET_ATTRIBUTE],
            attributeOldValue: true,
        });
        document.addEventListener('DOMContentLoaded', () => {
            const nodes = document.querySelectorAll(`[${TARGET_ATTRIBUTE}]`);
            for (let i = 0; i < nodes.length; i++) {
                const element = nodes[i];
                this.applyController(null, element);
            }
        }, false);
    }
    setResolver(resolver) { this.resolver = resolver; }
    registerController(controller, alias) {
        alias = document.currentScript?.getAttribute(ALIAS_KEY) || alias;
        if (alias) {
            this.resolved[alias] = Promise.resolve(controller);
            return;
        }
        console.error(`Controller registered without a name`, {
            script: document.currentScript,
            source: document.currentScript?.getAttribute('src'),
        });
    }
    mutationHandler(mutations) {
        for (const mutation of mutations) {
            const target = mutation.target;
            const isElementChange = mutation.type == 'childList';
            const isAttrChange = mutation.type == 'attributes';
            if (!isElementChange && !isAttrChange) {
                continue;
            }
            if (isElementChange) {
                if (hasControllerAttr(mutation.target)) {
                    this.applyController(null, mutation.target);
                }
                for (let i = 0; i < mutation.removedNodes.length; i++) {
                    const node = mutation.removedNodes[i];
                    if (hasControllerAttr(node)) {
                        this.detachController(node);
                    }
                }
                for (let i = 0; i < mutation.addedNodes.length; i++) {
                    const node = mutation.addedNodes[i];
                    if ('querySelectorAll' in node) {
                        if (hasControllerAttr(node)) {
                            this.applyController(null, node);
                        }
                        node
                            .querySelectorAll(`[${TARGET_ATTRIBUTE}]`)
                            .forEach(element => this.applyController(null, element));
                    }
                }
                continue;
            }
            this.applyController(mutation.oldValue, target);
        }
    }
    applyController(oldController, node) {
        const element = node;
        const currentValue = element.getAttribute(TARGET_ATTRIBUTE);
        if (currentValue === oldController || element[CONTROLLER_NAME_KEY] === currentValue) {
            return;
        }
        if (element[CONTROLLER_NAME_KEY]) {
            this.detachController(element);
        }
        if (!currentValue) {
            return;
        }
        if (!(currentValue in this.resolved)) {
            console.warn(`Controller "${currentValue}" not found in registry. Attempting to resolve...`);
            this.resolved[currentValue] = this.resolver(currentValue);
        }
        const resolved = this.resolved[currentValue];
        resolved.catch(err => console.error(`Error resolving controller "${currentValue}"`, err));
        resolved.then(res => {
            if (oldController === currentValue || element[CONTROLLER_NAME_KEY] === currentValue)
                return;
            let controller;
            try {
                controller = new res();
                controller.element = element;
            }
            catch (error) {
                console.error(`Error instantiating controller "${currentValue}"`, error);
                return;
            }
            try {
                controller.attach(element);
                element[CONTROLLER_KEY] = controller;
                element[CONTROLLER_NAME_KEY] = currentValue;
                element.dispatchEvent(new CustomEvent(this.EVENT_ATTACHED, {
                    detail: { alias: currentValue, controller }
                }));
            }
            catch (error) {
                console.error(`Error attaching controller "${currentValue}"`, error);
            }
        });
    }
    detachController(element) {
        const el = element;
        if (CONTROLLER_KEY in element) {
            const controller = el[CONTROLLER_KEY];
            if (controller && controller.detach) {
                const alias = el[CONTROLLER_NAME_KEY];
                controller.detach(controller.element);
                controller.element = null;
                delete el[CONTROLLER_KEY];
                delete el[CONTROLLER_NAME_KEY];
                element.dispatchEvent(new CustomEvent(this.EVENT_DETACHED, { detail: { alias, controller } }));
            }
        }
    }
    defaultResolver(controller) {
        const link = document.querySelector(`link[${ALIAS_KEY}="${controller}"]`);
        if (!link) {
            console.warn(`No link tag found for controller alias "${controller}"`);
            return Promise.reject(new Error(`No link alias found for controller "${controller}"`));
        }
        const href = link.getAttribute('href');
        if (!href) {
            return Promise.reject(new Error(`No href for controller "${controller}"`));
        }
        if (href in this.resolvedHrefs) {
            return this.resolvedHrefs[href];
        }
        let type = link.getAttribute('type-is');
        if (!type) {
            const rel = link.getAttribute('rel')?.toLowerCase();
            console.log(fileNameFromUrl(href));
            const ext = fileNameFromUrl(href).split('.').pop();
            if (rel === 'modulepreload' || ext === 'mjs') {
                type = 'module';
            }
            else {
                type = 'script';
            }
        }
        console.log(`Resolving controller "${controller}" of type "${type}" from href "${href}"`);
        switch (type.toLowerCase()) {
            case 'script': {
                const script = document.createElement('script');
                script.setAttribute(ALIAS_KEY, controller);
                let resolve;
                let reject;
                const result = new Promise((res, rej) => {
                    resolve = res;
                    reject = rej;
                });
                this.resolvedHrefs[href] = result;
                this.resolved[controller] = result;
                script.onload = () => {
                    if (!(controller in this.resolved)) {
                        return reject(new Error(`No controller registered for "${controller}"`));
                    }
                    this.resolved[controller]
                        .then(resolve)
                        .catch(reject);
                };
                script.onerror = (err) => {
                    console.error(`Failed to load script for "${controller}": ${href}`, err);
                    delete this.resolvedHrefs[href];
                    delete this.resolved[controller];
                    reject(err);
                };
                script.src = href;
                document.head.appendChild(script);
                return result;
            }
            case 'module': {
                const url = new URL(href, window.location.href).href;
                const imported = import(url)
                    .then(mod => mod.default)
                    .catch(err => {
                    console.error(`Failed to import module for "${controller}": ${url}`, err);
                    delete this.resolvedHrefs[href];
                    delete this.resolved[controller];
                    throw err;
                });
                this.resolvedHrefs[href] = imported;
                this.resolved[controller] = imported;
                return imported;
            }
            default:
                return Promise.reject(new Error(`Unsupported controller type "${type}" for "${controller}"`));
        }
    }
}
function hasControllerAttr(node) {
    if ('getAttribute' in node) {
        return !!(node.getAttribute(TARGET_ATTRIBUTE));
    }
    return false;
}
function fileNameFromUrl(url) {
    try {
        const parsed = new URL(url, window.location.href);
        const path = parsed.pathname;
        return path.substring(path.lastIndexOf('/') + 1);
    }
    catch {
        return url.split('/').pop()?.split('?')[0].split('#')[0] || '';
    }
}
const instance = new DomController();
setTimeout(() => {
    if (!('DomController' in window)) {
        window.DomController = instance;
    }
    if ('__DomControllers' in window) {
        window
            .__DomControllers
            ?.forEach((register) => register());
        delete window.__DomControllers;
    }
}, 0);
export default instance;
//# sourceMappingURL=index.js.map